import { onBeforeUnmount, onMounted, watch, type Ref, type WatchStopHandle } from 'vue'

export function useSiteMotion(root: Ref<HTMLElement | null>, disabled: Ref<boolean>) {
  const cleanups: Array<() => void> = []
  let motionVersion = 0
  let scrollTriggerRegistered = false
  let stopDisabledWatch: WatchStopHandle | undefined

  function stopMotion() {
    motionVersion += 1
    cleanups.splice(0).forEach((cleanup) => cleanup())
    if (root.value) root.value.dataset.siteMotion = 'disabled'
  }

  async function startMotion() {
    const version = ++motionVersion
    const element = root.value
    if (disabled.value || !element) return
    const [{ gsap }, { ScrollTrigger }, { default: Lenis }] = await Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger'),
      import('lenis'),
    ])

    if (version !== motionVersion || disabled.value || root.value !== element) return
    if (scrollTriggerRegistered) {
      ScrollTrigger.enable()
    } else {
      gsap.registerPlugin(ScrollTrigger)
      scrollTriggerRegistered = true
    }
    gsap.ticker.wake()

    const lenis = new Lenis({
      duration: 1.05,
      smoothWheel: true,
      wheelMultiplier: 0.86,
      touchMultiplier: 1,
    })
    let rafId = 0
    const tick = (time: number) => {
      lenis.raf(time)
      rafId = requestAnimationFrame(tick)
    }
    const handleVisibility = () => {
      cancelAnimationFrame(rafId)
      rafId = 0
      if (document.hidden) {
        lenis.stop()
        return
      }
      lenis.start()
      rafId = requestAnimationFrame(tick)
    }
    document.addEventListener('visibilitychange', handleVisibility)
    rafId = requestAnimationFrame(tick)

    const context = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((element) => {
        gsap.fromTo(
          element,
          { y: 54, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1.05,
            ease: 'power3.out',
            scrollTrigger: { trigger: element, start: 'top 88%', once: true },
          },
        )
      })

      gsap.to('.site-progress__bar', {
        scaleY: 1,
        ease: 'none',
        scrollTrigger: { start: 0, end: 'max', scrub: 0.2 },
      })

      const hero = element.querySelector<HTMLElement>('.hero-section')
      if (hero) {
        gsap.to(['.hero-section__foreground', '.hero-section__depth', '.hero-section__halo'], {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom 38%',
            scrub: 0.35,
          },
        })
      }

      const lifecycle = element.querySelector<HTMLElement>('#lifecycle')
      if (lifecycle) {
        ScrollTrigger.create({
          trigger: lifecycle,
          start: 'top 72%',
          end: 'bottom 38%',
          onUpdate: ({ progress }) => {
            window.dispatchEvent(new CustomEvent('se:lifecycle-progress', { detail: progress }))
          },
        })
      }
    }, element)

    cleanups.push(() => {
      cancelAnimationFrame(rafId)
      document.removeEventListener('visibilitychange', handleVisibility)
      lenis.destroy()
      context.revert()
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
      ScrollTrigger.disable()
      gsap.ticker.sleep()
      element.dataset.siteMotion = 'disabled'
    })
    element.dataset.siteMotion = 'active'
  }

  onMounted(() => {
    stopDisabledWatch = watch(
      disabled,
      (isDisabled) => {
        stopMotion()
        if (!isDisabled) void startMotion()
      },
      { immediate: true },
    )
  })

  onBeforeUnmount(() => {
    stopDisabledWatch?.()
    stopMotion()
  })
}
